import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginComponent } from '@security/login/login.component';
import { AssetListComponent } from 'src/app/organisation-assets/asset-list/asset-list.component';
import { WatchListComponent } from '@components/watch-list/watch-list.component';
import { EventsComponent } from '@components/events/events/events.component';
import { SettingsComponent } from '@components/site-settings/settings.component';
import { HelpComponent } from '@components/help/help.component';
import { NotificationsComponent } from '@components/notifications/notifications.component';
import { HomeDefaultPageComponent } from '@layout/home-default-page/home-default-page.component';
import { AssetDetailsComponent } from 'src/app/organisation-assets/asset-details/asset-details.component';
import { ProfileComponent } from '@admin/users/profile/profile.component';
import { AuthGuard } from '@auth0/auth0-angular';
import { LogoutComponent } from '@security/logout/logout.component';
import { AccountdisabledComponent } from '@admin/users/accountdisabled/accountdisabled.component';
import { ErrorComponent } from './error/error.component';
import { AccountManageComponent } from '@admin/accounts/account-manage/account-manage.component';
import { AccountsListComponent } from '@admin/accounts/accounts-list/accounts-list.component';
import { HierarchyDashboardComponent } from './organisations/hierarchy/hierarchy-dashboard/hierarchy-dashboard.component';

import { HomeNavigationComponent } from '@layout/home-navigation/home-navigation.component';
import { HeirarchyManagementComponent } from './organisations/hierarchy/management/heirarchy-management/heirarchy-management.component';
import { AccountInviteComponent } from '@admin/accounts/account-invite/account-invite.component';
import { DataIntegrationsComponent } from './integrations/data-integrations/data-integrations.component';
import { RouteFolderComponent } from '@components/route-folder/route-folder.component';
import { EventDetailsComponent } from '@components/events/event-details/event-details.component';
import { DataIntegrationsDetailComponent } from './integrations/data-integrations-detail/data-integrations-detail.component';
import { RulesComponent } from '@components/rules/rules/rules.component';
import { RuleDetailsComponent } from '@components/rules/rule-details/rule-details.component';
import { ProfileManageComponent } from '@admin/users/profile-manage/profile-manage.component';
import { ActionListComponent } from '@components/actions/action-list/action-list.component';
import { UserFavouritesComponent } from '@components/user-favourites/user-favourites.component';
import { ActionDetailsComponent } from '@components/actions/action-details/action-details.component';
import { AssetComparisonComponent } from './organisation-assets/asset-comparison/asset-comparison.component';
import { OrganisationFlaggedAssetsComponent } from '@components/organisation-flagged-assets/organisation-flagged-assets.component';
import { UnverifiedEmailComponent } from '@admin/users/unverified-email/unverified-email.component';
import { AccountManageFeaturesComponent } from '@admin/accounts/account-manage-features/account-manage-features.component';
import { AccountAdminGuard, AdminGuard, FeatureGuard } from '@services/authentication.service';
import { UnauthorisedComponent } from '@admin/users/unauthorised/unauthorised.component';
import { ModelsComponent } from '@components/models/models/models.component';
import { RoutingComponent } from '@components/routing/routing.component';
import { RedirectComponent } from '@components/redirect/redirect.component';
import { ModelDetailsComponent } from '@components/models/model-details/model-details.component';
import { EnergyDashboardComponent } from './organisations/energy-dashboard/energy-dashboard.component';
import { EnergyReportingComponent } from '@components/energy-reporting/energy-reporting.component';
import { OrganisationDataModelComponent } from './organisations/hierarchy/management/organisation-data-model/organisation-data-model.component';
import { DashboardCreationComponent } from './organisations/dashboard/dashboard-creation/dashboard-creation.component';
import { CustomDashboardComponent } from './organisations/dashboard/custom-dashboard/custom-dashboard.component';
import { VirtualSensorsComponent } from '@components/virtual-sensors/virtual-sensors.component';
import { VirtualSensorDetailsComponent } from '@components/virtual-sensor-details/virtual-sensor-details.component';
import { SAPCaseListComponent } from './cmms/sap-case-list/sap-case-list.component';
import { AccountFeatures } from '@services/accounts.service';
import { SAPCaseDetailComponent } from 'src/app/cmms/sap-case-detail/sap-case-detail.component';
import { HistoricalReportsComponent } from '@components/historical-reports/historical-reports.component';

/** Build the master root pages */
const routes: Routes = [
	// title: "Asset Management App" ,
	{
		path: '', component: HomeNavigationComponent, children: [
			// { path: '', redirectTo: 'dashboard', pathMatch: 'full', title: "My Dashboard" },
			// { path: '', component: DefaultPageComponent, canActivate: [AuthGuard] },
			{ path: '', component: HomeDefaultPageComponent, },

			{
				path: 'users-management', component: ProfileManageComponent, canActivate: [AuthGuard, AdminGuard],
				children: [
					{ path: ':userGUID', component: ProfileComponent, canActivate: [AuthGuard] },
				]
			},

			{ path: ':account/organisation/:organisation/event', component: EventsComponent, canActivate: [AuthGuard] },
			{ path: ':account/organisation/:organisation/event/:eventID', component: EventDetailsComponent, canActivate: [AuthGuard] },

			{ path: ':account/organisation/:organisation/rules', component: RulesComponent, canActivate: [AuthGuard] },
			{ path: ':account/organisation/:organisation/rules/:ruleID', component: RuleDetailsComponent, canActivate: [AuthGuard] },

			{
				path: ':account/organisation/:organisation/actions', component: ActionListComponent, canActivate: [AuthGuard, FeatureGuard],
				data: { featureID: AccountFeatures.Actions }
			},
			{
				path: ':account/organisation/:organisation/actions/:actionID', component: ActionDetailsComponent, canActivate: [AuthGuard, FeatureGuard],
				data: { featureID: AccountFeatures.Actions }
			},

			{
				path: ':account/organisation/:organisation/watch', component: AssetListComponent, canActivate: [AuthGuard, FeatureGuard],
				data: { featureID: AccountFeatures.Watch_List },
				children: [{
					path: ':assetID', component: WatchListComponent, canActivate: [AuthGuard, FeatureGuard],
					data: { featureID: AccountFeatures.Watch_List }
				}]
			},

			{ path: ':account/organisation/:organisation/models', component: ModelsComponent, canActivate: [AuthGuard] },
			{ path: ':account/organisation/:organisation/models/:modelID', component: ModelDetailsComponent, canActivate: [AuthGuard] },

			{ path: ':account/organisation/:organisation/favourites', component: UserFavouritesComponent, canActivate: [AuthGuard] },

			{
				path: ':account/organisation/:organisation/compare', component: AssetComparisonComponent, canActivate: [AuthGuard, FeatureGuard],
				data: { featureID: AccountFeatures.Compare_Assets }
			},

			{
				path: ':account/organisation/:organisation/SAPCases', component: SAPCaseListComponent, canActivate: [AuthGuard, FeatureGuard],
				data: { featureID: AccountFeatures.SAP }
			},
			{
				path: ':account/organisation/:organisation/SAPCases/:caseID', component: SAPCaseDetailComponent, canActivate: [AuthGuard, FeatureGuard],
				data: { featureID: AccountFeatures.SAP }
			},

			{ path: ':account/organisation/:organisation/flagged', component: OrganisationFlaggedAssetsComponent, canActivate: [AuthGuard] },

			// {
			// 	path: 'accounts-management', component: AccountsListComponent, canActivate: [AuthGuard],
			// 	children: [
			// 		{ path: ':accountID', component: AccountManageComponent, canActivate: [AuthGuard] },
			// 	]
			// },
			{
				path: 'accounts-management/:accountID', component: AccountManageComponent, canActivate: [AuthGuard]
			},

			{
				path: 'feature-management', component: AccountManageFeaturesComponent, canActivate: [AuthGuard, AdminGuard],
			},

			{ path: 'profile', component: ProfileComponent, canActivate: [AuthGuard] },
			{ path: 'settings', component: SettingsComponent, canActivate: [AuthGuard] },

			{ path: 'help', component: HelpComponent, canActivate: [AuthGuard] },
			{ path: 'notifications', component: NotificationsComponent, canActivate: [AuthGuard] },

			{
				path: 'setup', component: RouteFolderComponent, canActivate: [AuthGuard],
				children: [
					{ path: 'heirarchy-management/:organisation', component: HeirarchyManagementComponent, canActivate: [AuthGuard, AccountAdminGuard], },
					{
						path: 'integration-management/:organisation', component: DataIntegrationsComponent, canActivate: [AuthGuard, AccountAdminGuard],
						children: [
							{ path: 'integration/:integrationID', component: DataIntegrationsDetailComponent, canActivate: [AuthGuard, AccountAdminGuard] }
						]
					},
					{ path: 'data-model-management/:organisation', component: OrganisationDataModelComponent, canActivate: [AuthGuard, AccountAdminGuard], },
					{ path: 'dashboard-management/:organisation', component: DashboardCreationComponent, canActivate: [AuthGuard, AccountAdminGuard], },
					{ path: 'virtual-datapoint-management/:organisation', component: VirtualSensorsComponent, canActivate: [AuthGuard, FeatureGuard], data: { feature: 'Virtual Sensors' } },
					{ path: 'virtual-datapoint-management/:organisation/virtual-datapoint/:virtualSensorID', component: VirtualSensorDetailsComponent, canActivate: [AuthGuard] }
				]
			},

			/* Organisation Needs to be after the Fixed Routes */
			{ path: ':account/organisation/:organisation', component: HomeDefaultPageComponent },
			{ path: ':account/organisation/:organisation/dashboard', component: CustomDashboardComponent, canActivate: [AuthGuard] },
			{
				path: ':account/organisation/:organisation/energy-dashboard', component: EnergyDashboardComponent, canActivate: [AuthGuard, FeatureGuard],
				data: { featureID: AccountFeatures.Energy_Monitoring }
			},
			{
				path: ':account/organisation/:organisation/utility-reporting', component: EnergyReportingComponent, canActivate: [AuthGuard, FeatureGuard],
				data: { featureID: AccountFeatures.Energy_Monitoring }
			},
			{
				path: ':account/organisation/:organisation/utility-report-history', component: HistoricalReportsComponent, canActivate: [AuthGuard, FeatureGuard],
				data: { featureID: AccountFeatures.Energy_Monitoring }
			},
			{
				path: ':account/organisation/:organisation', component: RouteFolderComponent, canActivate: [AuthGuard],
				children: [
					{
						path: 'asset/:assetID', component: AssetDetailsComponent, canActivate: [AuthGuard]
					},
					{ path: 'events', component: EventsComponent, canActivate: [AuthGuard] }
				]
			},
			{
				path: ':account/organisation/:organisation/location/:sublocation', component: HierarchyDashboardComponent, canActivate: [AuthGuard],
				children: [
					{
						path: 'asset/:assetID', component: AssetDetailsComponent, canActivate: [AuthGuard]
					}
				]
			},
			{ path: ':account/organisation/:organisation/assets', component: AssetListComponent, canActivate: [AuthGuard] },

			{ path: 'invite', component: AccountInviteComponent, canActivate: [AuthGuard] },

			{ path: 'routing', component: RoutingComponent },

			/* These redirect to new routes  - Can be removed at some point as using OLD Routing*/
			{ path: 'organisations/:organisation', component: RedirectComponent },
			{ path: 'organisations/:organisation/dashboard', component: RedirectComponent, canActivate: [AuthGuard] },
			{
				path: 'organisations/:organisation', component: RedirectComponent, canActivate: [AuthGuard],
				children: [
					{
						path: 'asset/:assetID', component: RedirectComponent, canActivate: [AuthGuard]
					}
				]
			},
			{
				path: 'organisations/:organisation/location/:sublocation', component: RedirectComponent, canActivate: [AuthGuard],
				children: [
					{
						path: 'asset/:assetID', component: RedirectComponent, canActivate: [AuthGuard]
					}
				]
			}

		]
	},


	{ path: 'accountdisabled', component: AccountdisabledComponent },
	{ path: 'unverified', component: UnverifiedEmailComponent },
	{ path: 'unauthorised', component: UnauthorisedComponent },
	{ path: 'error', component: ErrorComponent },

	{ path: 'login', component: LoginComponent },
	{ path: 'logout', component: LogoutComponent },

	// { path: '', component: LandingPageComponent, title: "Asset Management App" },
	//{ path: '', redirectTo: 'login', pathMatch: 'full', title: "Malone Group Asset Site" },

];

@NgModule({
	imports: [RouterModule.forRoot(routes, {
		scrollPositionRestoration: 'disabled', // Retain scrolling position
		// anchorScrolling: 'enabled',
		// onSameUrlNavigation: 'reload',
		// scrollOffset: [0, 150], // Adjust scroll offset as needed
	})],
	// imports: [RouterModule.forRoot(routes)],
	exports: [RouterModule]
})
export class AppRoutingModule { }
